/**
 * Consensus Data Types
 *
 * Defines the data contracts for multi-model consensus analysis.
 * Supports Heatmap, Narrative, and Wheel views.
 *
 * Part of Prompt 2: Bootstrap Consensus data shape + selector
 */

/**
 * Consensus criteria for analysis
 * Based on common LLM evaluation dimensions
 */
export type Criterion =
  | 'factual_accuracy'
  | 'opinion_balance'
  | 'tone_appropriateness'
  | 'completeness'
  | 'safety';

/**
 * Model identifier (modelId + providerId)
 * Format: "providerId:modelId" (e.g., "openai:gpt-4")
 */
export type ModelKey = string;

/**
 * Per-model consensus state for a criterion
 * - agree: All models align on this criterion
 * - mixed: Some agreement, some disagreement
 * - differ: Models significantly diverge
 */
export type PerModelState = 'agree' | 'mixed' | 'differ';

/**
 * Criterion analysis result
 */
export interface CriterionAnalysis {
  criterion: Criterion;
  label: string; // Human-readable label
  description: string;
  perModelState: Record<ModelKey, PerModelState>;
  summary: string; // Brief narrative summary
  consensusScore: number; // 0-100, how much models agree
}

/**
 * Model response with consensus metadata
 */
export interface ConsensusResponse {
  modelKey: ModelKey;
  modelId: string;
  providerId: string;
  response: string;
  criterionStates: Record<Criterion, PerModelState>;
  overallScore?: number;
}

/**
 * Pairwise verdict for 2-model comparisons
 * Part of: Heatmap Consensus Enhancements (Task 3)
 */
export type PairwiseVerdict = 'agree' | 'partial' | 'disagree';

/**
 * Pairwise verdict analysis (only when exactly 2 models)
 * Part of: Heatmap Consensus Enhancements (Task 3)
 */
export interface PairwiseAnalysis {
  modelAKey: ModelKey;
  modelBKey: ModelKey;
  criterionVerdicts: Record<Criterion, PairwiseVerdict>;
  summary: {
    agreeCount: number;
    partialCount: number;
    disagreeCount: number;
  };
}

/**
 * Complete consensus data for a comparison
 */
export interface ConsensusData {
  comparisonId: string;
  criteria: CriterionAnalysis[];
  responses: ConsensusResponse[];
  narratives: Record<Criterion, string>; // Detailed narratives per criterion
  overallConsensus: number; // 0-100, overall agreement level
  winnerId?: string; // Highest scoring response ID (modelKey format)
  pairwiseAnalysis?: PairwiseAnalysis; // Task 3: Only present when exactly 2 models
}

/**
 * Criterion metadata for UI display
 */
export const CRITERION_METADATA: Record<Criterion, { label: string; description: string; icon: string }> = {
  factual_accuracy: {
    label: 'Factual Accuracy',
    description: 'Correctness of facts and information',
    icon: '✓'
  },
  opinion_balance: {
    label: 'Opinion Balance',
    description: 'Fair representation of viewpoints',
    icon: '⚖'
  },
  tone_appropriateness: {
    label: 'Tone',
    description: 'Appropriate communication style',
    icon: '🎭'
  },
  completeness: {
    label: 'Completeness',
    description: 'Thoroughness of response',
    icon: '📋'
  },
  safety: {
    label: 'Safety',
    description: 'Absence of harmful content',
    icon: '🛡'
  }
};
